import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { searchMetadataPage, type SearchMatchParams } from '../api/search.api'
import { searchQueryKeys } from '../api/queryKeys'
import type { SearchEntityHit, SearchMetadataKind, SearchMetadataPageResponse } from '../models'
import { METADATA_PAGE_SIZE, METADATA_PREFETCH_BATCHES, VISIBLE_CAP } from '../logic/constants'
import type { MetadataSectionSource } from '../logic/globalSearchSections'
import { getMetadataIdentity } from '../logic/rows'
import {
  SEARCH_PAGE_QUERY_OPTIONS,
  logFailure,
  retryFailedPage,
  useFetchPagesWhile,
} from './searchPaging'
import { useScopedState } from './useScopedState'

interface MetadataSectionPagesOptions {
  kind: SearchMetadataKind
  query: string
  matchParams: SearchMatchParams
  /** The search the section belongs to: a new one shows three rows again. */
  scope: string
  /** The section's rows in the search response. */
  firstRows: SearchEntityHit[]
  /** The section's count in the search response. */
  total: number | undefined
}

/**
 * A metadata section's "See more" on the All tab: each press shows ten more
 * rows, and pages of ten load one after another from where the loaded rows end
 * until METADATA_PREFETCH_BATCHES pages wait beyond the shown ones.
 */
export function useMetadataSectionPages({
  kind,
  query,
  matchParams,
  scope,
  firstRows,
  total,
}: MetadataSectionPagesOptions) {
  const [revealed, setRevealed] = useScopedState<number | undefined>(scope, undefined)
  const pagesQuery = useInfiniteQuery({
    queryKey: searchQueryKeys.globalSearchPages(kind, query, matchParams),
    queryFn: ({ pageParam }) =>
      searchMetadataPage(kind, query, pageParam, METADATA_PAGE_SIZE, matchParams).catch(
        logFailure('Failed to load more search metadata')
      ),
    initialPageParam: firstRows.length,
    getNextPageParam: (lastPage, pages) =>
      lastPage.has_more && lastPage.items.length > 0
        ? mergeMetadataRows(kind, firstRows, pages).length
        : undefined,
    // Loaded only on request, below.
    enabled: false,
    ...SEARCH_PAGE_QUERY_OPTIONS,
  })

  const pages = pagesQuery.data?.pages
  const rows = useMemo(() => mergeMetadataRows(kind, firstRows, pages), [kind, firstRows, pages])
  const lastPage = pages?.[pages.length - 1]
  const hasMore = lastPage ? lastPage.has_more : rows.length < (total ?? rows.length)
  useFetchPagesWhile(
    pagesQuery,
    revealed !== undefined &&
      revealed > VISIBLE_CAP &&
      hasMore &&
      rows.length - revealed < METADATA_PAGE_SIZE * METADATA_PREFETCH_BATCHES
  )

  const source = useMemo<MetadataSectionSource>(
    () => ({ rows, total, revealed, hasMore }),
    [rows, total, revealed, hasMore]
  )

  return {
    /** What the section shows: its rows so far, how many are revealed, and whether more exist. */
    source,
    revealMore: () => {
      setRevealed((current) => (current ?? VISIBLE_CAP) + METADATA_PAGE_SIZE)
      retryFailedPage(pagesQuery)
    },
    seeLess: () => setRevealed(VISIBLE_CAP),
  }
}

/** The response's rows, then each page's rows not already among them. */
function mergeMetadataRows(
  kind: SearchMetadataKind,
  firstRows: SearchEntityHit[],
  pages: SearchMetadataPageResponse[] | undefined
): SearchEntityHit[] {
  if (!pages?.length) return firstRows
  const rows = [...firstRows]
  const seen = new Set(firstRows.map((item) => getMetadataIdentity(kind, item)))
  for (const page of pages) {
    const fresh = page.items.filter((item) => !seen.has(getMetadataIdentity(kind, item)))
    fresh.forEach((item) => seen.add(getMetadataIdentity(kind, item)))
    rows.push(...fresh)
  }
  return rows
}
